import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { announcementsApi, type Announcement } from '../../services/announcementsApi'
import { AnnouncementPreviewModal } from './AnnouncementPreviewModal'

const PRIORITY_CONFIG = {
  info: { label: 'Info', bg: 'bg-blue-500/15', text: 'text-blue-400', dot: 'bg-blue-400' },
  warning: { label: 'Warning', bg: 'bg-amber-500/15', text: 'text-amber-400', dot: 'bg-amber-400' },
  critical: { label: 'Critical', bg: 'bg-red-500/15', text: 'text-red-400', dot: 'bg-red-400' },
} as const

const STATUS_CONFIG = {
  active: { label: 'Active', bg: 'bg-green-500/15', text: 'text-green-400' },
  scheduled: { label: 'Scheduled', bg: 'bg-blue-500/15', text: 'text-blue-400' },
  expired: { label: 'Expired', bg: 'bg-slate-600/50', text: 'text-slate-400' },
} as const

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

export function AnnouncementView(): React.ReactElement {
  const { id } = useParams<{ id: string }>()
  const [announcement, setAnnouncement] = useState<Announcement | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [showPreview, setShowPreview] = useState(false)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    async function load() {
      setIsLoading(true)
      setError('')
      try {
        const data = await announcementsApi.getAnnouncement(id!)
        if (!cancelled) setAnnouncement(data)
      } catch {
        if (!cancelled) setError('Failed to load announcement')
      }
      if (!cancelled) setIsLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [id])

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-slate-400 text-sm mt-3">Loading announcement...</span>
      </div>
    )
  }

  if (error || !announcement) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/30 rounded-lg">
        <span className="text-red-300 text-sm">{error || 'Announcement not found'}</span>
      </div>
    )
  }

  const priority = PRIORITY_CONFIG[announcement.priority]
  const status = STATUS_CONFIG[announcement.status]

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/control_center/announcements"
          className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white transition-colors mb-3"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Back to Announcements
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2.5 flex-wrap mb-2">
              <h1 className="text-2xl font-bold text-white">{announcement.title}</h1>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${priority.bg} ${priority.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${priority.dot}`} />
                {priority.label}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${status.bg} ${status.text}`}>
                {status.label}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <span>Created by {announcement.created_by_name}</span>
              <span>{formatDate(announcement.created_at)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Preview
            </button>
            <Link
              to={`/control_center/announcements/${announcement.id}/edit`}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
              </svg>
              Edit
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Body */}
        <div className="lg:col-span-2">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Message</h2>
            {announcement.body ? (
              <div className="text-slate-300 text-sm whitespace-pre-wrap leading-relaxed">{announcement.body}</div>
            ) : (
              <p className="text-slate-500 text-sm italic">No message body</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Schedule */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Schedule</h2>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-500">Starts</p>
                <p className="text-sm text-white">{formatDate(announcement.starts_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Ends</p>
                <p className="text-sm text-white">{announcement.ends_at ? formatDate(announcement.ends_at) : 'Indefinite'}</p>
              </div>
            </div>
          </div>

          {/* Target */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Target</h2>
            {announcement.is_system_wide ? (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                </svg>
                <span className="text-sm text-white">System-wide</span>
              </div>
            ) : announcement.target_accounts && announcement.target_accounts.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">{announcement.target_accounts.length} account{announcement.target_accounts.length !== 1 ? 's' : ''}</p>
                <div className="space-y-1.5">
                  {announcement.target_accounts.map(acct => (
                    <Link
                      key={acct.id}
                      to={`/control_center/accounts/${acct.id}`}
                      className="flex items-center gap-2 px-3 py-2 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <span className="text-sm text-white">{acct.name}</span>
                      <svg className="w-3.5 h-3.5 text-slate-500 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">No target specified</p>
            )}
          </div>

          {/* Acknowledgments */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
              Acknowledgments
              {announcement.acknowledgment_count > 0 && (
                <span className="ml-2 text-emerald-400">{announcement.acknowledgment_count}</span>
              )}
            </h2>
            {announcement.acknowledgment_count === 0 ? (
              <p className="text-sm text-slate-500 italic">No acknowledgments yet</p>
            ) : announcement.acknowledgments && announcement.acknowledgments.length > 0 ? (
              <div className="space-y-2">
                {announcement.acknowledgments.map(ack => (
                  <div key={ack.user_id} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300">{ack.user_name}</span>
                    <span className="text-xs text-slate-500">{formatDate(ack.acknowledged_at)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">{announcement.acknowledgment_count} user{announcement.acknowledgment_count !== 1 ? 's' : ''} acknowledged</p>
            )}
          </div>
        </div>
      </div>

      {showPreview && (
        <AnnouncementPreviewModal
          title={announcement.title}
          body={announcement.body}
          priority={announcement.priority}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  )
}
